import { defineStore, getActivePinia } from 'pinia'
import { login } from '@/libs/api/auth/login'
import { refresh } from '@/libs/api/auth/refresh'

import router from '@/router'
import { useSettings } from '@/store/settings'
import { useSockets } from '@/libs/composables/sockets'
import {
  publishSessionUpdated,
  publishSessionLogout,
} from '@/libs/composables/sessionBroadcast'

export const useAuth = defineStore('auth', {
  state: () => ({
    // Default token fields
    access_token: null,
    refresh_token: null,
    ws_token: null,

    // Base auth fields
    email: null,
    password: null,
    isSessionExpired: false,

    // Helper
    refreshPromise: null,
  }),

  getters: {
    accessToken: (state) => state.access_token?.token,
    refreshToken: (state) => state.refresh_token?.token,
    wsToken: (state) => state.ws_token?.token,
    baseAuthEnoughData: (state) => !!(state.email && state.password),
  },

  actions: {
    setAuthTokens(payload) {
      this.access_token = payload?.access
      this.refresh_token = payload?.refresh
    },

    setWsToken(payload) {
      this.ws_token = payload
    },

    clearAuthData() {
      useSockets().destroy()
      this.resetStore()
    },

    /**
     *
     * @param params
     * @param validate
     * @returns {Promise<void>}
     */
    async login(params, validate = true) {
      this.setBaseAuth(params)

      const response = await login(params, validate)

      const adaptedResponse =
        !validate && response?.status === 200
          ? { ...response, data: { status: 'SUCCESS', data: response.data } }
          : response

      const result = adaptedResponse?.data

      if (result.status === 'SUCCESS') {
        this.setAuthTokens(result.data)

        this.unsetBaseAuth(params)
        this.isSessionExpired = false
        publishSessionUpdated({ email: params?.email })
      }

      return adaptedResponse
    },

    async logout(byTimeout = false) {
      this.clearAuthData()
      await useSettings().getSettings()
      await router.push({ name: 'Login' })
      this.isSessionExpired = byTimeout
      publishSessionLogout()
    },

    /**
     * Reset all stores
     */
    resetStore() {
      const excludeStoreList = [
        'modalsStore',
        'settings',
        'fingerprint',
        'locator',
        'countries',
        'expiredDocuments',
        'preferences',
      ]
      getActivePinia()._s.forEach((store) => {
        if (!excludeStoreList.includes(store.$id)) {
          store.$reset()
        }
      })
    },

    /**
     * Refresh tokens
     *
     * @returns {Promise<any>}
     */
    async refresh(tokenOverride) {
      if (this.refreshPromise) {
        return this.refreshPromise
      }

      const tokenToUse = tokenOverride || this.refreshToken

      this.refreshPromise = refresh(tokenToUse)
        .then((response) => {
          this.setAuthTokens(response.data)
          return response
        })
        .catch(async (err) => {
          await this.logout(true)
          throw err
        })
        .finally(() => {
          this.refreshPromise = null
        })

      return this.refreshPromise
    },

    setBaseAuth({ email, password }) {
      this.email = email
      this.password = password
    },

    unsetBaseAuth() {
      this.email = null
      this.password = null
    },
  },
  persist: {
    storage: localStorage,
    paths: ['access_token', 'refresh_token', 'ws_token'],
  },
  share: {
    enable: true,
  },
})
